import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

const updatedDate = "2026-08-14";
const updatedDateLabel = "14 August 2026";

function Privacy() {
  const informationGroups = [
    {
      title: "Identity & contact",
      copy: "Your name, email, phone number, delivery and billing addresses, and any account details you choose to provide.",
    },
    {
      title: "Orders & payments",
      copy: "Products, sizes, order value, payment status, returns and customer-care history. Payment card details are handled by our payment providers.",
    },
    {
      title: "Device & usage",
      copy: "IP address, browser, device, pages viewed, referring links and approximate location collected through essential and optional technologies.",
    },
    {
      title: "Preferences",
      copy: "Wishlist activity, saved choices, newsletter consent and the way you interact with our messages when you have opted in.",
    },
  ];

  const useRows = [
    [
      "Fulfil your order",
      "Contact, order and payment status",
      "Perform our contract with you",
    ],
    [
      "Support returns and enquiries",
      "Order and conversation history",
      "Perform our contract and provide service",
    ],
    [
      "Keep the store secure",
      "Device, usage and transaction signals",
      "Our legitimate interest in preventing fraud",
    ],
    [
      "Improve our experience",
      "Aggregated usage and feedback",
      "Our legitimate interest, or consent where required",
    ],
    [
      "Send considered updates",
      "Email and communication preferences",
      "Your consent; you may withdraw it at any time",
    ],
    [
      "Meet legal duties",
      "Invoices, transactions and required records",
      "Compliance with law",
    ],
  ];

  const rights = [
    "Ask for a copy of the personal information we hold about you.",
    "Correct information that is incomplete or inaccurate.",
    "Request deletion or restriction where the law allows it.",
    "Object to certain uses, including direct marketing.",
    "Withdraw consent without affecting earlier lawful processing.",
    "Ask for portable data and lodge a complaint with your local authority.",
  ];

  const sections = [
    {
      id: "scope",
      title: "Scope & responsibility",
      content: (
        <>
          <p>
            This notice explains how {siteConfig.brandName} collects, uses,
            shares and protects personal information when you visit our
            storefront, place an order or contact our team. It applies to this
            website and the services linked from it.
          </p>
          <p>
            For the activity described here, {siteConfig.brandName} is
            responsible for deciding how your information is used. Our
            correspondence address is {siteConfig.contact.address}.
          </p>
          <aside className="policy-note" aria-label="Privacy summary">
            <span className="eyebrow">Our approach</span>
            <p>
              Collect only what is useful, explain why it is needed, and keep
              your choices within reach.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: "collection",
      title: "Information we collect",
      content: (
        <>
          <p>
            Most information comes directly from you. Some is created as you use
            the store, and some may be supplied by trusted partners such as
            payment, delivery or fraud-prevention providers.
          </p>
          <div className="policy-card-grid">
            {informationGroups.map((group) => (
              <article className="policy-card" key={group.title}>
                <h3>{group.title}</h3>
                <p>{group.copy}</p>
              </article>
            ))}
          </div>
        </>
      ),
    },
    {
      id: "use",
      title: "How and why we use it",
      content: (
        <>
          <p>
            We connect each use of personal information to a clear business need
            and, where applicable, a lawful basis. We do not sell personal
            information for money.
          </p>
          <div
            className="policy-table-wrap"
            role="region"
            aria-label="How personal information is used"
            tabIndex={0}
          >
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">Purpose</th>
                  <th scope="col">Information</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {useRows.map(([purpose, data, reason]) => (
                  <tr key={purpose}>
                    <th scope="row">{purpose}</th>
                    <td>{data}</td>
                    <td>{reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ),
    },
    {
      id: "cookies",
      title: "Cookies & similar tools",
      content: (
        <>
          <p>
            Essential technologies remember your bag, maintain security and make
            checkout work. With your permission, optional analytics and
            marketing technologies help us understand performance and show more
            relevant communications.
          </p>
          <ul className="policy-list policy-list--divided">
            <li>
              <strong>Essential</strong>
              <span>
                Required for navigation, security, checkout and saved choices.
              </span>
            </li>
            <li>
              <strong>Analytics</strong>
              <span>
                Helps us understand visits and improve pages in aggregate.
              </span>
            </li>
            <li>
              <strong>Preference</strong>
              <span>Remembers region, language and other choices.</span>
            </li>
            <li>
              <strong>Marketing</strong>
              <span>
                Measures campaigns and is used only where consent is required
                and given.
              </span>
            </li>
          </ul>
          <p>
            You can also limit cookies in your browser, although essential store
            functions may then be unavailable.
          </p>
        </>
      ),
    },
    {
      id: "sharing",
      title: "Sharing & international transfers",
      content: (
        <>
          <p>
            We share only what is needed with service providers that help
            operate the store: hosting, payments, fraud prevention, delivery,
            customer care, analytics and email services. We may also disclose
            information when required by law, to protect people or the store, or
            as part of a business reorganisation.
          </p>
          <p>
            Providers act under contractual confidentiality and security duties.
            If information moves to a country with different privacy rules, we
            use recognised safeguards where the law requires them.
          </p>
        </>
      ),
    },
    {
      id: "retention",
      title: "Retention & security",
      content: (
        <>
          <p>
            We keep information only while it supports the purpose collected, an
            active relationship, or a legal, tax, accounting, fraud-prevention
            or dispute requirement. Retention periods vary by record; once no
            longer needed, information is deleted or anonymised.
          </p>
          <p>
            Administrative, technical and organisational measures are used to
            reduce the risk of loss, misuse or unauthorised access. No online
            system is risk-free, so please use a unique password and contact us
            promptly if you notice suspicious activity.
          </p>
        </>
      ),
    },
    {
      id: "rights",
      title: "Your choices & rights",
      content: (
        <>
          <p>Your rights depend on where you live. Where available, you may:</p>
          <ul className="policy-check-list">
            {rights.map((right) => (
              <li key={right}>{right}</li>
            ))}
          </ul>
          <p>
            Email{" "}
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>{" "}
            to make a request. We may need to verify your identity and will
            respond within the period required by law. You can unsubscribe from
            marketing through any message without affecting service emails.
          </p>
        </>
      ),
    },
    {
      id: "updates",
      title: "Children, updates & contact",
      content: (
        <>
          <p>
            Our store is not directed to children who cannot independently
            consent under local law, and we do not knowingly collect their
            information. If you believe a child has provided data, please tell
            us.
          </p>
          <p>
            We may revise this notice as our services or legal duties change.
            Material updates will be made visible on the store, and the date at
            the top will show when this notice was last revised.
          </p>
          <address className="policy-address">
            <strong>{siteConfig.brandName} privacy enquiries</strong>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>

            <span>{siteConfig.contact.address}</span>
            <span>{siteConfig.contact.hours}</span>
          </address>
        </>
      ),
    },
  ];

  return (
    <main
      className="privacy policy-page policy-page--privacy"
      aria-labelledby="privacy-title"
    >
      <header className="policy-hero page-shell">
        <div className="policy-hero__label">
          <p className="eyebrow">Customer care · 01</p>
          <span>Transparency, by design.</span>
        </div>
        <div className="policy-hero__copy">
          <h1 className="page-heading" id="privacy-title">
            Privacy,
            <br />
            <em>plainly.</em>
          </h1>
          <p>
            A clear account of the information we collect, why we use it, and
            the choices that remain yours.
          </p>
        </div>
        <div className="policy-hero__date">
          <span className="eyebrow">Last updated</span>
          <time dateTime={updatedDate}>{updatedDateLabel}</time>
        </div>
      </header>

      <div className="policy-layout page-shell">
        <aside className="policy-toc">
          <div className="policy-toc__inner">
            <p className="eyebrow">On this page</p>
            <nav aria-label="Privacy policy sections">
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="policy-toc__help">
              <span className="eyebrow">Need clarity?</span>
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </aside>

        <article className="policy-content">
          {sections.map((section, index) => (
            <section
              className="policy-section"
              id={section.id}
              aria-labelledby={`${section.id}-title`}
              key={section.id}
            >
              <span className="policy-section__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="policy-section__body">
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.content}
              </div>
            </section>
          ))}
        </article>
      </div>

      <section
        className="policy-next page-shell"
        aria-labelledby="privacy-next-title"
      >
        <div>
          <p className="eyebrow">Continue exploring</p>
          <h2 id="privacy-next-title">
            Good choices start with clear information.
          </h2>
        </div>
        <div className="policy-next__links">
          <Link className="button-light" to="/terms">
            Read our terms <span aria-hidden="true">→</span>
          </Link>
          <Link className="policy-next__text-link" to="/contact">
            Contact customer care <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Privacy;
